import { colors } from 'constants/colors';
import { Sparkles, X } from 'lucide-react';
import { useState } from 'react';
import styled from 'styled-components';

import { useAppVersion } from 'hooks/useAppVersion';

/**
 * Popup non bloquante annoncée à un onglet dont le bundle est périmé.
 *
 * Volontairement une proposition et pas un rechargement subi : un back-office est plein de
 * formulaires à moitié remplis, et recharger sous les doigts de l'utilisateur lui fait perdre sa
 * saisie. Le rechargement automatique existe, mais il est réservé aux onglets laissés de côté
 * (cf. `useAppVersion`).
 *
 * Non bloquante ne veut pas dire discrète : posée en surimpression, en vert de marque et avec une
 * entrée animée, parce qu'un bandeau blanc sur fond clair se confond avec la page et se rate.
 * C'est le mouvement, plus que la taille, qui attrape l'œil — d'où l'animation d'entrée, coupée
 * pour qui a demandé moins d'animations.
 */
export function NewVersionBanner() {
    const { isUpdateAvailable, reload } = useAppVersion();
    const [isDismissed, setIsDismissed] = useState(false);

    if (!isUpdateAvailable || isDismissed) {
        return null;
    }

    return (
        <Popup role="status" data-test="new-version-banner">
            <IconBadge aria-hidden="true">
                <Sparkles size={20} />
            </IconBadge>
            <Content>
                <Title>Une nouvelle version de Totadmin est disponible</Title>
                <Description>Rechargez la page pour l'installer. Les saisies en cours seront perdues.</Description>
            </Content>
            <ReloadButton type="button" onClick={reload} data-test="new-version-reload">
                Recharger
            </ReloadButton>
            <DismissButton
                type="button"
                onClick={() => setIsDismissed(true)}
                aria-label="Masquer"
                data-test="new-version-dismiss"
            >
                <X size={18} />
            </DismissButton>
        </Popup>
    );
}

const Popup = styled.div`
    position: fixed;
    top: 24px;
    left: 50%;
    /* Au-dessus de tout le chrome de l'app (max constaté : 1000), sous les toasts react-toastify. */
    z-index: 1001;
    display: flex;
    align-items: center;
    gap: 16px;
    max-width: calc(100vw - 32px);
    padding: 14px 14px 14px 20px;
    border-radius: 10px;
    background-color: ${colors.green};
    color: ${colors.black};
    box-shadow:
        0 16px 40px rgba(0, 0, 0, 0.24),
        0 2px 8px rgba(0, 0, 0, 0.12);
    animation: new-version-popup-in 350ms cubic-bezier(0.16, 1, 0.3, 1) both;

    @keyframes new-version-popup-in {
        from {
            transform: translate(-50%, -24px);
            opacity: 0;
        }
        to {
            transform: translate(-50%, 0);
            opacity: 1;
        }
    }

    @media (prefers-reduced-motion: reduce) {
        transform: translateX(-50%);
        animation: none;
    }
`;

const IconBadge = styled.div`
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background-color: ${colors.black};
    color: ${colors.green};
`;

const Content = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const Title = styled.span`
    font-size: 15px;
    font-weight: 700;
`;

const Description = styled.span`
    font-size: 13px;
    /* Le noir de marque à 70 % : lisible sur le vert, sans concurrencer le titre. */
    color: rgba(17, 17, 17, 0.7);
`;

const ReloadButton = styled.button`
    flex-shrink: 0;
    padding: 9px 18px;
    border: none;
    border-radius: ${({ theme }) => theme.borderRadius};
    background-color: ${colors.black};
    color: ${colors.pureWhite};
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;

    &:hover {
        background-color: ${colors.hoveredBlack};
    }
`;

const DismissButton = styled.button`
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    padding: 0;
    border: none;
    border-radius: ${({ theme }) => theme.borderRadius};
    background-color: transparent;
    color: ${colors.black};
    cursor: pointer;

    &:hover {
        background-color: rgba(17, 17, 17, 0.1);
    }
`;
